/**
 * Utility functions for program URL resolution, slug normalization, and registration matching.
 */

/**
 * Normalizes and extracts a clean program slug without route prefixes or leading slashes.
 * Useful for email aliases, URL routes, and lookup keys.
 *
 * @param {Object|string} programOrLink - Program document or link/slug string
 * @returns {string}
 */
export const getProgramSlug = (programOrLink) => {
    if (!programOrLink) return '';
    let linkStr = '';
    if (typeof programOrLink === 'string') {
        linkStr = programOrLink;
    } else if (typeof programOrLink === 'object') {
        const isExternalLink = typeof programOrLink.link === 'string' &&
            (programOrLink.link.startsWith('http://') || programOrLink.link.startsWith('https://'));
        linkStr = programOrLink.slug || (!isExternalLink && programOrLink.link) || programOrLink.id || '';
    }
    return linkStr
        .toLowerCase()
        .trim()
        .replace(/^\/+/, '')
        .replace(/^(programs|camps|races|events)\/+/, '')
        .replace(/^\/+/, '')
        .replace(/[^\w-]+/g, '-')
        .replace(/-+/g, '-')
        .replace(/(^-|-$)/g, '');
};

/**
 * Generates the canonical full-page event description URL for a given program.
 * Matches routes configured across Navbar and App:
 * - seasonal programs: /programs/:slug
 * - camps: /camps/:slug
 * - races: /races/:slug
 * 
 * @param {Object} program 
 * @returns {string}
 */
const BASE_PATH_BY_TYPE = {
    camp: 'camps',
    race: 'races',
    event: 'events',
};

const KNOWN_PREFIXES = ['programs', 'camps', 'races', 'events'];

const resolveCleanSlug = (program) => {
    let slug = program.slug || program.id || '';
    if (program.link) {
        let cleanLink = program.link.replace(/^\//, '');
        for (const prefix of KNOWN_PREFIXES) {
            if (cleanLink.startsWith(`${prefix}/`)) {
                cleanLink = cleanLink.replace(new RegExp(`^${prefix}/`), '');
                break;
            }
        }
        if (cleanLink) {
            slug = cleanLink;
        }
    }
    return slug;
};

/**
 * Generates the canonical full-page event description URL for a given program.
 * Matches routes configured across Navbar and App:
 * - seasonal programs: /programs/:slug
 * - camps: /camps/:slug
 * - races: /races/:slug
 * 
 * @param {Object} program 
 * @returns {string}
 */
export const getProgramPageUrl = (program) => {
    if (!program) return '/programs';

    // External URL handling
    if (typeof program.link === 'string' && (program.link.startsWith('http://') || program.link.startsWith('https://'))) {
        return program.link;
    }

    const basePath = BASE_PATH_BY_TYPE[program.type] || 'programs';

    if (typeof program.link === 'string' && KNOWN_PREFIXES.some((p) => program.link.startsWith(`/${p}/`))) {
        return program.link;
    }

    const slug = resolveCleanSlug(program);
    return `/${basePath}/${slug}`;
};

/**
 * Determines whether a registration belongs to a program.
 * Checks against the canonical document ID, current marketing link/slug,
 * and any historical slugs preserved in previousSlugs.
 *
 * @param {Object} r - Registration record
 * @param {Object} prog - Program document
 * @returns {boolean}
 */
export const isRegForProgram = (r, prog) => {
    if (!r || !prog) return false;
    if (r.programId === prog.id) return true;
    if (prog.link && r.programId === prog.link) return true;
    if (prog.slug && r.programId === prog.slug) return true;
    if (Array.isArray(prog.previousSlugs) && prog.previousSlugs.includes(r.programId)) return true;
    const progSlug = getProgramSlug(prog);
    if (progSlug && (r.programId === progSlug || getProgramSlug(r.programId) === progSlug)) return true;
    return false;
};
